import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { FileUpload } from '@/components/ui/file-upload';
import {
  Upload, Loader2, Check, AlertTriangle, Receipt,
  X, ChevronDown, ChevronUp, ClipboardCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { buildTimestamp, statementDateToYmd } from '@/lib/gt-repayments';
import type { BankStatementGroup, SbtPayment, GtNotification } from '@/lib/sbt-pdf';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SyncTransactionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type VehicleTransactionType = 'cif_30' | 'cif_70' | 'tax_bill' | 'port_charges' | 'landed_cost' | 'steering_changed';

const TYPE_LABELS: Record<VehicleTransactionType, string> = {
  cif_30: 'CIF 30%',
  cif_70: 'CIF 70%',
  tax_bill: 'Tax Bill',
  port_charges: 'Port Charges',
  landed_cost: 'Landed Cost',
  steering_changed: 'Steering Conversion',
};

export interface MatchedTransaction {
  reference: string;
  transDate: string;
  transactionTime: string | null;
  amount: number;
  fee: number;
  description: string;
  matchedVehicleId: number | null;
  matchedVehicleLabel: string | null;
  matchedStockId: string | null;
  suggestedType: VehicleTransactionType | null;
  alreadyImported: boolean;
  confidence: 'high' | 'low';
}

interface VehicleOption {
  id: number;
  label: string;
  stockId: string | null;
  cif: number | null;
}

// ─── Steps ───────────────────────────────────────────────────────────────────

const STEPS = [
  { key: 'upload', label: 'Upload Files', icon: Upload, description: 'Bank statement + SBT email' },
  { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Verify matched transactions' },
  { key: 'confirm', label: 'Confirm', icon: Check, description: 'Import into database' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatUsd(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

/**
 * Twin of the helper in `ImportTransactionsSheet`: the review row previews the day `buildTimestamp`
 * stores (this file calls it on the write path), never the browser's own reading of the raw string.
 * The operator's date override flows through here too, which is exactly the shape gate we want.
 */
function formatDate(dateStr: string): string {
  const ymd = statementDateToYmd(dateStr);
  if (!ymd) return dateStr;
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function getStockId(url: string | null): string | null {
  if (!url) return null;
  const segments = url.replace(/\/+$/, '').split('/');
  return segments[segments.length - 1] || null;
}

/** Suggest a transaction type based on amount relative to CIF. */
function suggestType(amount: number, cif: number | null): VehicleTransactionType | null {
  if (!cif || cif <= 0) return null;
  const ratio = amount / cif;
  if (ratio >= 0.25 && ratio <= 0.35) return 'cif_30';
  if (ratio >= 0.60 && ratio <= 0.80) return 'cif_70';
  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SyncTransactionsSheet({ open, onOpenChange }: SyncTransactionsSheetProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);

  // Step 1: Files
  const [bankFile, setBankFile] = useState<File | null>(null);
  const [sbtFiles, setSbtFiles] = useState<File[]>([]);
  const [gtFile, setGtFile] = useState<File | null>(null);

  // Processing
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');

  // Step 2: Review
  const [matches, setMatches] = useState<MatchedTransaction[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);

  // Overrides: keyed by reference
  const [vehicleOverrides, setVehicleOverrides] = useState<Record<string, number>>({});
  const [typeOverrides, setTypeOverrides] = useState<Record<string, VehicleTransactionType>>({});
  const [timeOverrides, setTimeOverrides] = useState<Record<string, string>>({});
  const [dateOverrides, setDateOverrides] = useState<Record<string, string>>({});

  // Step 3: Confirm
  const [confirming, setConfirming] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importSummary, setImportSummary] = useState('');

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(0);
      setBankFile(null);
      setSbtFiles([]);
      setGtFile(null);
      setMatches([]);
      setSelected(new Set());
      setVehicles([]);
      setVehicleOverrides({});
      setTypeOverrides({});
      setTimeOverrides({});
      setDateOverrides({});
      setProcessing(false);
      setConfirming(false);
      setImportDone(false);
      setImportSummary('');
    }, 300);
  };

  const isStepComplete = (i: number) => {
    switch (i) {
      case 0: return matches.length > 0;
      case 1: return selected.size > 0;
      case 2: return importDone;
      default: return false;
    }
  };

  // ─── Get effective values (with overrides) ────────────────────────────────

  const getVehicleId = useCallback((m: MatchedTransaction) => {
    return vehicleOverrides[m.reference] ?? m.matchedVehicleId;
  }, [vehicleOverrides]);

  const getType = useCallback((m: MatchedTransaction): VehicleTransactionType | null => {
    return typeOverrides[m.reference] ?? m.suggestedType;
  }, [typeOverrides]);

  const getTime = useCallback((m: MatchedTransaction) => {
    return timeOverrides[m.reference] ?? m.transactionTime;
  }, [timeOverrides]);

  const getDate = useCallback((m: MatchedTransaction) => {
    return dateOverrides[m.reference] ?? m.transDate;
  }, [dateOverrides]);

  // ─── Process PDFs ─────────────────────────────────────────────────────────

  const handleProcess = async () => {
    if (!bankFile || sbtFiles.length === 0) return;
    setProcessing(true);

    try {
      setProcessingStatus('Parsing bank statement...');
      const { parseUsdBankStatementXls, extractPdfText, parseSbtPaymentEmail, parseGtEmailNotifications } = await import('@/lib/sbt-pdf');

      // Parse bank statement (XLS — HTML-formatted spreadsheet from GTBank)
      const xlsText = await bankFile.text();
      const bankGroups = parseUsdBankStatementXls(xlsText);

      if (bankGroups.length === 0) {
        toast.error('No SBT transactions found', { description: 'The bank statement may not contain any SBT debit entries.' });
        setProcessing(false);
        return;
      }

      // Parse SBT payment emails (multiple files)
      setProcessingStatus('Parsing SBT payment emails...');
      const sbtPayments: Awaited<ReturnType<typeof parseSbtPaymentEmail>> = [];
      for (const sbtFile of sbtFiles) {
        const sbtText = await extractPdfText(sbtFile);
        sbtPayments.push(...parseSbtPaymentEmail(sbtText));
      }

      if (sbtPayments.length === 0) {
        toast.error('No payments found in SBT emails', { description: 'Could not extract RecNo and amounts from any file.' });
        setProcessing(false);
        return;
      }

      // Parse GT email (optional)
      let gtNotifications: GtNotification[] = [];
      if (gtFile) {
        setProcessingStatus('Parsing GT email notifications...');
        const gtText = await extractPdfText(gtFile);
        gtNotifications = parseGtEmailNotifications(gtText);
      }

      // Send to edge function for DB matching
      setProcessingStatus('Matching to vehicles...');
      const { data, error: fnError } = await supabase.functions.invoke('process-bank-statement', {
        body: {
          bankGroups: bankGroups.map((g) => ({
            reference: g.reference,
            transDate: g.transDate,
            mainAmount: g.main.debit,
            feeAmount: g.fee?.debit ?? 0,
            sbtId: g.main.sbtId,
            docNumber: g.main.docNumber,
            remarks: g.main.remarks,
          })),
          sbtPayments: sbtPayments.map((p) => ({
            recNo: p.recNo,
            amount: p.amount,
          })),
          gtNotifications: gtNotifications.map((n) => ({
            docNumber: n.docNumber,
            time: n.time,
            amount: n.amount,
          })),
        },
      });

      if (fnError) {
        // supabase-js wraps non-2xx as FunctionsHttpError — extract actual body
        let detail = fnError.message || 'Edge function error';
        if (fnError.context && typeof fnError.context.json === 'function') {
          try {
            const body = await fnError.context.json();
            detail = body?.error || JSON.stringify(body);
          } catch { /* ignore */ }
        }
        throw new Error(detail);
      }
      if (!data?.matches || data.matches.length === 0) {
        toast.warning('No matches found', { description: 'Could not match any transactions to vehicles.' });
        setProcessing(false);
        return;
      }

      setMatches(data.matches);
      setVehicles(data.vehicles || []);

      // Auto-select importable rows
      const autoSelected = new Set(
        data.matches
          .filter((m: MatchedTransaction) => !m.alreadyImported && m.matchedVehicleId && m.suggestedType)
          .map((m: MatchedTransaction) => m.reference)
      );
      setSelected(autoSelected);
      setStep(1);
    } catch (err) {
      toast.error('Failed to process PDFs', { description: (err as Error).message });
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  // ─── Confirm Import ──────────────────────────────────────────────────────

  const handleConfirm = async () => {
    setConfirming(true);

    try {
      const toImport = matches.filter((m) => selected.has(m.reference) && !m.alreadyImported);
      let imported = 0;

      for (const m of toImport) {
        const vehicleId = getVehicleId(m);
        const type = getType(m);
        const time = getTime(m);

        if (!vehicleId || !type) continue;

        const date = getDate(m);
        const timestamp = buildTimestamp(date, time);

        // Insert transaction
        const { data: txn, error: txnErr } = await supabase
          .from('transactions')
          .insert({
            amount: m.amount,
            fee: m.fee > 0 ? m.fee : null,
            reference: m.reference,
            description: m.description,
            transaction_at: timestamp,
            type: 'DEBIT',
            currency: 'USD',
          })
          .select('id')
          .single();

        if (txnErr) {
          console.error(`Failed to insert transaction ${m.reference}:`, txnErr);
          continue;
        }

        // Insert vehicle_transaction link
        const { error: vtErr } = await supabase
          .from('vehicle_transactions')
          .insert({
            transaction_id: txn.id,
            vehicle_id: vehicleId,
            transaction_type: type,
          });

        if (vtErr) {
          console.error(`Failed to link transaction to vehicle:`, vtErr);
          continue;
        }

        imported++;
      }

      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles-count'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-stages'] });

      setImportSummary(`${imported} transaction${imported !== 1 ? 's' : ''} imported`);
      setImportDone(true);
      setStep(2);
      toast.success('Import complete', { description: `${imported} transactions imported.` });
    } catch (err) {
      toast.error('Import failed', { description: (err as Error).message });
    } finally {
      setConfirming(false);
    }
  };

  // ─── Toggle helpers ────────────────────────────────────────────────────────

  const selectableMatches = useMemo(
    () => matches.filter((m) => !m.alreadyImported),
    [matches]
  );

  const toggleRow = (ref: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(selected.size === selectableMatches.length
      ? new Set()
      : new Set(selectableMatches.map((m) => m.reference))
    );
  };

  // ─── Step Content ──────────────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (step) {
      case 0: // Upload
        return (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-2">
                Bank Statement (USD)
                <Badge variant="secondary" className="text-[10px] px-1.5 h-4">Required</Badge>
              </label>
              <FileUpload file={bankFile} setFile={setBankFile} accept=".xls" maxSizeMB={10} />
              <p className="text-xs text-muted-foreground">GTBank statement XLS file with SBT transactions.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-2">
                SBT Payment Emails
                <Badge variant="secondary" className="text-[10px] px-1.5 h-4">Required</Badge>
              </label>
              <FileUpload files={sbtFiles} setFiles={setSbtFiles} accept=".pdf" maxSizeMB={10} multiple />
              <p className="text-xs text-muted-foreground">SBT "Deposit Payment Due" email threads exported as PDF. Upload one or more files.</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-2">
                GT Email Notification
                <Badge variant="outline" className="text-[10px] px-1.5 h-4 text-muted-foreground">Optional</Badge>
              </label>
              <FileUpload file={gtFile} setFile={setGtFile} accept=".pdf" maxSizeMB={10} />
              <p className="text-xs text-muted-foreground">Provides accurate transaction times. Leave empty to use bank statement date.</p>
            </div>
          </div>
        );

      case 1: // Review
        return (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                {selectableMatches.length} new
              </Badge>
              {matches.filter((m) => m.alreadyImported).length > 0 && (
                <Badge variant="outline" className="bg-muted text-muted-foreground">
                  {matches.filter((m) => m.alreadyImported).length} already imported
                </Badge>
              )}
              {matches.filter((m) => m.confidence === 'low').length > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {matches.filter((m) => m.confidence === 'low').length} need review
                </Badge>
              )}
            </div>

            {/* Table */}
            <div className="border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-max w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-3 py-2 text-left w-8">
                        <Checkbox
                          checked={selected.size === selectableMatches.length && selectableMatches.length > 0}
                          onCheckedChange={toggleAll}
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Ref</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Vehicle</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">Amount</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">Fee</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Type</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((m) => {
                      const isImported = m.alreadyImported;
                      const isSelected = selected.has(m.reference);
                      const effectiveVehicleId = getVehicleId(m);
                      const effectiveType = getType(m);
                      const effectiveTime = getTime(m);
                      const vehicleLabel = vehicleOverrides[m.reference]
                        ? vehicles.find((v) => v.id === vehicleOverrides[m.reference])?.label ?? '—'
                        : m.matchedVehicleLabel;

                      return (
                        <tr
                          key={m.reference}
                          className={cn(
                            'border-b last:border-0 transition-colors',
                            isImported ? 'opacity-40 bg-muted/10' :
                              isSelected ? 'bg-white' : 'bg-muted/20 opacity-60',
                            m.confidence === 'low' && !isImported && 'bg-amber-50/30'
                          )}
                        >
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleRow(m.reference)}
                              disabled={isImported}
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{m.reference}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {isImported ? (
                              <span className="text-xs text-muted-foreground">{formatDate(m.transDate)}</span>
                            ) : (
                              <Input
                                value={getDate(m)}
                                onChange={(e) => setDateOverrides((prev) => ({ ...prev, [m.reference]: e.target.value }))}
                                className="h-7 text-xs w-28 font-mono"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {isImported ? (
                              <span className="text-xs text-muted-foreground">{effectiveTime || '—'}</span>
                            ) : (
                              <Input
                                value={effectiveTime || ''}
                                onChange={(e) => setTimeOverrides((prev) => ({ ...prev, [m.reference]: e.target.value }))}
                                placeholder="e.g. 4:16 PM"
                                className="h-7 text-xs w-24 font-mono"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {isImported ? (
                              <span className="text-xs">{vehicleLabel || '—'}</span>
                            ) : (
                              <Select
                                value={String(effectiveVehicleId ?? '')}
                                onValueChange={(v) => {
                                  const vid = parseInt(v);
                                  setVehicleOverrides((prev) => ({ ...prev, [m.reference]: vid }));
                                  // Re-suggest type based on new vehicle's CIF
                                  const vehicle = vehicles.find((veh) => veh.id === vid);
                                  if (vehicle?.cif) {
                                    const suggested = suggestType(m.amount, vehicle.cif);
                                    if (suggested) {
                                      setTypeOverrides((prev) => ({ ...prev, [m.reference]: suggested }));
                                    }
                                  }
                                }}
                              >
                                <SelectTrigger className="h-7 text-xs w-48">
                                  <SelectValue placeholder="Select vehicle" />
                                </SelectTrigger>
                                <SelectContent>
                                  {vehicles.map((v) => (
                                    <SelectItem key={v.id} value={String(v.id)} className="text-xs">
                                      {v.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">{formatUsd(m.amount)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap text-muted-foreground">
                            {m.fee > 0 ? formatUsd(m.fee) : '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {isImported ? (
                              <span className="text-xs">{effectiveType ? TYPE_LABELS[effectiveType] : '—'}</span>
                            ) : (
                              <Select
                                value={effectiveType ?? ''}
                                onValueChange={(v) => setTypeOverrides((prev) => ({ ...prev, [m.reference]: v as VehicleTransactionType }))}
                              >
                                <SelectTrigger className="h-7 text-xs w-32">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                                    <SelectItem key={key} value={key} className="text-xs">{label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">
                            {isImported ? (
                              <Badge variant="secondary" className="text-[10px]">Imported</Badge>
                            ) : m.confidence === 'low' ? (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                                Review
                              </Badge>
                            ) : !effectiveVehicleId || !effectiveType ? (
                              <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">Missing</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Ready</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 2: // Done
        return (
          <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold">Import Complete</h3>
            <p className="text-muted-foreground text-sm max-w-xs">{importSummary}</p>
          </div>
        );
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="!inset-y-4 !right-4 !h-auto !p-0 !w-[90vw] !max-w-5xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
      >
        {/* Left Sidebar */}
        <div className="w-[280px] bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden flex-shrink-0">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

          <div className="mb-6 relative z-10">
            <h2 className="text-2xl font-semibold tracking-tight">Sync Transactions</h2>
            <p className="text-sm text-muted-foreground mt-1">Import SBT bank deposits</p>
          </div>

          <div className="space-y-1 relative z-10 flex-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isComplete = isStepComplete(i);

              return (
                <button
                  key={s.key}
                  onClick={() => { if (i <= step || isStepComplete(i - 1)) setStep(i); }}
                  disabled={i > step && !isStepComplete(i - 1)}
                  className={cn(
                    'w-full group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 text-left',
                    isActive ? 'bg-background shadow-sm ring-1 ring-border' : 'hover:bg-background/50',
                    i > step && !isStepComplete(i - 1) && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className="relative">
                    <div className={cn(
                      'h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300',
                      isActive ? 'bg-primary text-primary-foreground' :
                        isComplete ? 'bg-green-100 text-green-600/70' : 'bg-muted text-muted-foreground',
                    )}>
                      <Icon className={cn('h-5 w-5', isComplete && !isActive && 'opacity-60')} />
                    </div>
                    {isComplete && !isActive && (
                      <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center ring-2 ring-background">
                        <Check className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className={cn('font-medium text-sm transition-colors', isActive ? 'text-foreground' : 'text-muted-foreground')}>{s.label}</p>
                    <p className="text-xs text-muted-foreground/70 hidden lg:block">{s.description}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-auto flex items-center relative z-10">
            <Button variant="ghost" size="sm" className="w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent" onClick={handleClose}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 flex flex-col h-full relative min-w-0">
          {/* Mobile header */}
          <div className="md:hidden p-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold">{STEPS[step].label}</h2>
              <p className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}><X className="h-5 w-5" /></Button>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex flex-col h-full w-full">
              <div className="flex-none mb-4 px-6 md:px-8 pt-6 md:pt-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                    <p className="text-muted-foreground">{STEPS[step].description}</p>
                  </div>
                  {isStepComplete(step) && (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      <Check className="h-3.5 w-3.5" />
                      Complete
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-auto px-6 md:px-8 pb-6">
                {renderStepContent()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
            <div className="flex items-center justify-between">
              {step === 0 && (
                <>
                  <Button variant="outline" onClick={handleClose} className="rounded-full">Cancel</Button>
                  <Button
                    onClick={handleProcess}
                    disabled={!bankFile || sbtFiles.length === 0 || processing}
                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    {processing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{processingStatus}</>
                    ) : (
                      <><Receipt className="h-4 w-4 mr-2" />Process Files</>
                    )}
                  </Button>
                </>
              )}

              {step === 1 && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setStep(0); setMatches([]); }}
                    className="rounded-full"
                  >
                    ← Back
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    disabled={selected.size === 0 || confirming}
                    className="rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                  >
                    {confirming ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</>
                    ) : (
                      <><Check className="h-4 w-4 mr-2" />Import {selected.size} transaction{selected.size !== 1 ? 's' : ''}</>
                    )}
                  </Button>
                </>
              )}

              {step === 2 && (
                <>
                  <div />
                  <Button onClick={handleClose} className="rounded-full shadow-lg hover:shadow-primary/25 transition-all">Done</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
